/**
 * Tenant-agnostic cost rollups derived from the canonical agent list.
 * The Agent record already carries `monthlyCostAud`, `monthlyValueAud`,
 * `per1kToken`, `businessUnit`, `vendorId` and `model`, so every consumer
 * (Cost Optimisation, Vendor Performance, Home dashboard) should derive
 * its view here instead of maintaining a parallel hand-rolled rollup.
 *
 * All helpers are pure — give them the agents array and they return a
 * sorted, ready-to-render list.
 */

/** Top-N governed burners with their ROI multiple. */
export function topBurners(agents, n = 5) {
  return agents
    .filter((a) => a.agentType !== "shadow" && a.monthlyCostAud > 0)
    .slice()
    .sort((a, b) => (b.monthlyCostAud || 0) - (a.monthlyCostAud || 0))
    .slice(0, n)
    .map((a) => ({
      ...a,
      roiMultiple: a.monthlyCostAud ? (a.monthlyValueAud || 0) / a.monthlyCostAud : 0,
    }));
}

/**
 * Group governed agents by `businessUnit` (the label the Cost Optimisation
 * page already exposes) and sum spend / value. Returns the same shape the
 * static map used so the UI is a drop-in: `{ name, agents, spendAud,
 * valueAud, roi, highRisk }[]`, sorted by spend descending.
 */
export function burnByBusinessUnit(agents) {
  const map = new Map();
  for (const a of agents) {
    if (a.agentType === "shadow") continue;
    const key = a.businessUnit || a.department || "Unassigned";
    const entry = map.get(key) || { name: key, agents: 0, spendAud: 0, valueAud: 0, highRisk: 0 };
    entry.agents += 1;
    entry.spendAud += a.monthlyCostAud || 0;
    entry.valueAud += a.monthlyValueAud || 0;
    if (a.riskBand === "high") entry.highRisk += 1;
    map.set(key, entry);
  }
  return Array.from(map.values())
    .map((e) => ({ ...e, roi: e.spendAud ? e.valueAud / e.spendAud : 0 }))
    .sort((a, b) => b.spendAud - a.spendAud);
}

/**
 * Vendor-level rollup that adds a *weighted average* $/1k-token cost,
 * weighted by each agent's monthly burn (heavy-spending agents on the
 * same vendor pull the blended rate). Returns vendors enriched with
 * `agentCount`, `spendAud`, `valueAud`, `weightedPer1kToken`, sorted by
 * monthly spend descending. Vendors with no governed agents return
 * `weightedPer1kToken: null`.
 */
export function vendorBurnRollup(vendors, agents) {
  return vendors
    .map((v) => {
      const vendorAgents = agents.filter(
        (a) => a.vendorId === v.id && a.agentType !== "shadow",
      );
      const spend = vendorAgents.reduce((s, a) => s + (a.monthlyCostAud || 0), 0);
      const value = vendorAgents.reduce((s, a) => s + (a.monthlyValueAud || 0), 0);
      const weightedPer1k =
        spend > 0
          ? vendorAgents.reduce(
              (s, a) => s + (a.per1kToken || 0) * (a.monthlyCostAud || 0),
              0,
            ) / spend
          : null;
      return {
        ...v,
        agentCount: vendorAgents.length,
        spendAud: spend,
        valueAud: value,
        weightedPer1kToken: weightedPer1k,
      };
    })
    .sort((a, b) => (b.spendAud || 0) - (a.spendAud || 0));
}

// ────────────────────────────────────────────────────────────────────
// Cost optimisation nudges
// ────────────────────────────────────────────────────────────────────

// Conservative model down-shift catalogue. Each entry pairs an expensive
// model with a smaller sibling on the same vendor + a 2026 enterprise
// blended rate for the cheaper SKU. We deliberately keep the list short
// so we never recommend an across-vendor swap that needs procurement /
// MSA re-work — those belong on the Cost Optimisation page, not as an
// inline nudge.
const MODEL_DOWNGRADE = {
  "claude-3.5-sonnet": { to: "claude-3-haiku",     toPer1k: 0.0012, qualityImpact: "needs A/B test" },
  "claude-3-sonnet":   { to: "claude-3-haiku",     toPer1k: 0.0012, qualityImpact: "needs A/B test" },
  "gpt-4o":            { to: "gpt-4o-mini",        toPer1k: 0.0008, qualityImpact: "minimal on summarisation" },
  "mistral-large":     { to: "mistral-small",      toPer1k: 0.0025, qualityImpact: "needs A/B test" },
  "gemini-1.5-pro":    { to: "gemini-1.5-flash",   toPer1k: 0.0010, qualityImpact: "minimal on extraction" },
};

/**
 * Returns a structured nudge for an agent, or null if no nudge is
 * warranted. Rules (deliberately conservative so this stays advisory):
 *   - Agent must be governed (skip shadow) and currently `live` or `monitored`.
 *   - Monthly burn ≥ A$20k (cheap agents rarely justify a model swap).
 *   - Agent autonomy ≠ "Act" (we never propose model changes for
 *     autonomous-action agents — they require re-certification).
 *   - High-risk agents are excluded — Legal/CRO must approve their model.
 *   - The downgrade must save ≥ 30% of the agent's `per1kToken` rate
 *     and ≥ A$5k per month in absolute terms.
 */
export function optimisationNudge(agent) {
  if (!agent || agent.agentType === "shadow") return null;
  if (agent.status !== "live" && agent.status !== "monitored") return null;
  if ((agent.monthlyCostAud || 0) < 20000) return null;
  if (agent.autonomy === "Act") return null;
  if (agent.riskBand === "high") return null;

  const dg = MODEL_DOWNGRADE[agent.model];
  if (!dg) return null;

  const fromRate = agent.per1kToken || 0;
  if (fromRate <= dg.toPer1k) return null;

  const ratio = (fromRate - dg.toPer1k) / fromRate;
  if (ratio < 0.3) return null;

  const savePerMo = Math.round((agent.monthlyCostAud || 0) * ratio);
  if (savePerMo < 5000) return null;

  // Round to nearest A$1k to avoid spurious precision in a nudge.
  const savePerMoRounded = Math.round(savePerMo / 1000) * 1000;

  return {
    fromModel: agent.model,
    toModel: dg.to,
    savePerMo: savePerMoRounded,
    qualityImpact: dg.qualityImpact,
  };
}
